<script setup>
/* global axios */
// Relatórios do Kanban/CRM: totais por lead_status, valor de vendas, breakdown
// por funil/etapa e principais motivos de ganho/perda.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const data = ref(null);
const loading = ref(true);

const money = cents =>
  ((Number(cents) || 0) / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: 'BRL',
  });
const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);

const load = async () => {
  loading.value = true;
  try {
    data.value = (await axios.get(`${base}/reports`)).data;
  } catch (e) {
    data.value = null;
  } finally {
    loading.value = false;
  }
};
onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-6xl px-8 py-6 mx-auto">
      <h1 class="mb-1 text-heading-1 text-n-slate-12">
        {{ t('CRM_REPORTS.title') }}
      </h1>
      <p class="mb-6 text-sm text-n-slate-11">{{ t('CRM_REPORTS.subtitle') }}</p>

      <div v-if="loading" class="py-16 text-sm text-center text-n-slate-10">
        …
      </div>

      <template v-else-if="data">
        <!-- cards de resumo -->
        <div class="grid grid-cols-2 gap-4 mb-6 md:grid-cols-3 lg:grid-cols-6">
          <div
            v-for="m in [
              { k: 'total', v: data.summary.total, c: 'text-n-slate-12' },
              { k: 'open', v: data.summary.open, c: 'text-n-blue-11' },
              { k: 'won', v: data.summary.won, c: 'text-n-teal-11' },
              { k: 'lost', v: data.summary.lost, c: 'text-n-ruby-11' },
            ]"
            :key="m.k"
            class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <div class="text-2xl font-semibold" :class="m.c">{{ m.v }}</div>
            <div class="text-xs text-n-slate-11">
              {{ t(`CRM_REPORTS.summary.${m.k}`) }}
            </div>
          </div>
          <div
            class="col-span-2 p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <div class="text-2xl font-semibold text-n-teal-11">
              {{ money(data.summary.won_value) }}
            </div>
            <div class="text-xs text-n-slate-11">
              {{ t('CRM_REPORTS.summary.wonValue') }}
            </div>
            <div class="mt-1 text-xs text-n-slate-10">
              {{ t('CRM_REPORTS.summary.totalValue') }}:
              {{ money(data.summary.total_value) }}
            </div>
          </div>
        </div>

        <!-- funis -->
        <div class="grid grid-cols-1 gap-4 mb-6 lg:grid-cols-2">
          <div
            v-for="f in data.funnels"
            :key="f.id"
            class="p-5 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <div class="flex items-baseline justify-between mb-3">
              <h3 class="font-medium text-n-slate-12">{{ f.name }}</h3>
              <span class="text-xs text-n-slate-10"
                >{{ f.cards }} · {{ money(f.value) }}</span
              >
            </div>
            <div class="flex flex-col gap-2">
              <div v-for="s in f.stages" :key="s.id">
                <div class="flex justify-between mb-0.5 text-xs">
                  <span class="text-n-slate-11">{{ s.name }}</span>
                  <span class="text-n-slate-10"
                    >{{ s.cards }} · {{ money(s.value) }}</span
                  >
                </div>
                <div class="h-1.5 rounded-full bg-n-alpha-2">
                  <div
                    class="h-1.5 rounded-full bg-n-brand"
                    :style="{ width: `${pct(s.cards, f.cards)}%` }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- motivos -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
            <h3 class="mb-3 font-medium text-n-teal-11">
              {{ t('CRM_REPORTS.reasons.won') }}
            </h3>
            <p
              v-if="!data.reasons.won.length"
              class="text-sm text-n-slate-10"
            >
              {{ t('CRM_REPORTS.reasons.empty') }}
            </p>
            <div
              v-for="r in data.reasons.won"
              :key="r.reason"
              class="flex justify-between py-1 text-sm border-b border-n-weak last:border-0"
            >
              <span class="text-n-slate-12">{{ r.reason }}</span>
              <span class="font-medium text-n-slate-11">{{ r.count }}</span>
            </div>
          </div>
          <div class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
            <h3 class="mb-3 font-medium text-n-ruby-11">
              {{ t('CRM_REPORTS.reasons.lost') }}
            </h3>
            <p
              v-if="!data.reasons.lost.length"
              class="text-sm text-n-slate-10"
            >
              {{ t('CRM_REPORTS.reasons.empty') }}
            </p>
            <div
              v-for="r in data.reasons.lost"
              :key="r.reason"
              class="flex justify-between py-1 text-sm border-b border-n-weak last:border-0"
            >
              <span class="text-n-slate-12">{{ r.reason }}</span>
              <span class="font-medium text-n-slate-11">{{ r.count }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
